import React, { useState, useEffect } from 'react';
import { useApp } from '../../context/AppContext.tsx';
import { X, UserCheck, Save, RotateCcw, CheckCircle2, Info, HelpCircle } from 'lucide-react';

interface AuthorBioModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const DEFAULT_AUTHOR_BIO_ROLE = 'Tim Advokat & Penasihat Hukum';
export const DEFAULT_AUTHOR_BIO_NOTE =
  'Artikel ini ditelaah dan dipublikasikan secara resmi oleh {author} di Kantor Hukum ALY LAW FIRM untuk memberikan wawasan, analisis, dan solusi hukum yang terpercaya bagi masyarakat dan klien.';

export default function AuthorBioModal({ isOpen, onClose }: AuthorBioModalProps) {
  const { settings, updateSettingsOnServer, showToast, user } = useApp();

  const [role, setRole] = useState(
    settings?.articleAuthorBioRole || DEFAULT_AUTHOR_BIO_ROLE
  );
  const [note, setNote] = useState(
    settings?.articleAuthorBioNote || DEFAULT_AUTHOR_BIO_NOTE
  );
  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setRole(settings?.articleAuthorBioRole || DEFAULT_AUTHOR_BIO_ROLE);
      setNote(settings?.articleAuthorBioNote || DEFAULT_AUTHOR_BIO_NOTE);
      setSaveSuccess(false);
    }
  }, [isOpen, settings?.articleAuthorBioRole, settings?.articleAuthorBioNote]);

  if (!isOpen) return null;

  const handleReset = () => {
    setRole(DEFAULT_AUTHOR_BIO_ROLE);
    setNote(DEFAULT_AUTHOR_BIO_NOTE);
    showToast('Teks dikembalikan ke format resmi ALY LAW FIRM.', 'info');
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!note.trim()) {
      showToast('Catatan bio penulis tidak boleh kosong.', 'warning');
      return;
    }

    setIsSaving(true);
    try {
      const ok = await updateSettingsOnServer({
        articleAuthorBioRole: role.trim() || DEFAULT_AUTHOR_BIO_ROLE,
        articleAuthorBioNote: note.trim(),
      });

      if (ok) {
        setSaveSuccess(true);
        showToast('Teks Catatan & Bio Penulis Artikel berhasil diperbarui!', 'success', 'Bio Tersimpan');
        setTimeout(() => {
          onClose();
        }, 800);
      }
    } catch (err) {
      console.error(err);
      showToast('Gagal menyimpan pengaturan bio penulis.', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const previewAuthorName = user?.name || 'Ali Yusuf, S.H.';
  const previewNote = note.replace(/\{author\}/g, previewAuthorName);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-xs animate-in fade-in duration-200">
      <div
        className="bg-white w-full max-w-2xl rounded-3xl shadow-2xl border border-slate-100 overflow-hidden flex flex-col max-h-[92vh] animate-in zoom-in-95 duration-200"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="p-5 sm:p-6 bg-gradient-to-r from-amber-700 via-amber-800 to-slate-900 text-white relative">
          <button
            type="button"
            onClick={onClose}
            className="absolute top-4 right-4 w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors cursor-pointer"
            title="Tutup"
          >
            <X className="w-4 h-4" />
          </button>

          <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-amber-300 mb-1">
            <UserCheck className="w-4 h-4" />
            <span>Kustomisasi Teks Halaman Artikel</span>
          </div>
          <h3 className="text-lg sm:text-xl font-black text-white">
            Ubah Teks Catatan Bio & Validasi Penulis
          </h3>
          <p className="text-xs text-amber-100/90 mt-1">
            Teks ini muncul otomatis di bawah setiap naskah artikel pada kartu profil penulis (Author Bio Card).
          </p>
        </div>

        {/* Content Form */}
        <form onSubmit={handleSave} className="p-5 sm:p-6 overflow-y-auto space-y-5 flex-1">
          {saveSuccess && (
            <div className="p-3 bg-emerald-50 border border-emerald-300 rounded-xl flex items-center gap-2 text-xs font-bold text-emerald-800 animate-in fade-in">
              <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
              <span>Perubahan berhasil disimpan dan telah aktif di seluruh halaman artikel!</span>
            </div>
          )}

          {/* Form Role */}
          <div className="space-y-1.5">
            <label className="text-xs font-bold text-slate-800 flex items-center justify-between">
              <span>Peran / Jabatan Penulis (Badge Label):</span>
              <span className="text-[11px] text-slate-400 font-normal">Contoh: Founder & Advokat Utama</span>
            </label>
            <input
              type="text"
              value={role}
              onChange={(e) => setRole(e.target.value)}
              placeholder="Tim Advokat & Penasihat Hukum"
              className="w-full px-3.5 py-2.5 text-xs bg-slate-50 border border-slate-300 rounded-xl focus:ring-2 focus:ring-amber-500 focus:outline-none font-semibold text-slate-900"
            />
          </div>

          {/* Form Note */}
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <label className="text-xs font-bold text-slate-800">
                Teks Catatan Validasi & Bio Penulis:
              </label>
              <button
                type="button"
                onClick={handleReset}
                className="text-[11px] text-amber-700 hover:text-amber-900 font-bold flex items-center gap-1 cursor-pointer"
              >
                <RotateCcw className="w-3 h-3" />
                <span>Reset ke Standar ALY LAW FIRM</span>
              </button>
            </div>
            <textarea
              rows={4}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Tulis kalimat catatan bio di sini..."
              className="w-full p-3.5 text-xs bg-slate-50 border border-slate-300 rounded-xl focus:ring-2 focus:ring-amber-500 focus:outline-none text-slate-900 leading-relaxed font-normal"
            />
            <div className="p-3 bg-blue-50/80 border border-blue-200 rounded-xl flex items-start gap-2.5 text-[11px] text-blue-900">
              <Info className="w-4 h-4 text-blue-600 shrink-0 mt-0.5" />
              <div className="space-y-0.5">
                <span className="font-bold">Tips Penulisan Dinamis:</span>
                <p className="text-blue-800 leading-relaxed">
                  Gunakan kode <code className="bg-white px-1.5 py-0.5 rounded font-mono font-bold text-blue-700 border border-blue-200">{"{author}"}</code> di dalam kalimat untuk memunculkan nama penulis artikel secara otomatis (misalnya: Ali Yusuf, S.H.). Anda juga dapat mengetik teks tanpa kode tersebut.
                </p>
              </div>
            </div>
          </div>

          {/* Live Preview Card */}
          <div className="space-y-2">
            <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500 block">
              Pratinjau Tampilan di Halaman Artikel Publik:
            </span>
            <div className="p-4 rounded-2xl border border-slate-200 bg-white shadow-2xs flex items-start gap-3.5">
              <div className="w-12 h-12 rounded-full bg-slate-900 text-amber-400 font-black text-sm flex items-center justify-center shrink-0 border-2 border-white shadow-xs">
                {previewAuthorName.charAt(0)}
              </div>
              <div className="flex-1 min-w-0">
                <span className="text-[10px] font-black uppercase tracking-wider px-2 py-0.5 rounded bg-emerald-50 text-emerald-800 border border-emerald-200 inline-block mb-1">
                  {role || DEFAULT_AUTHOR_BIO_ROLE}
                </span>
                <h4 className="text-sm font-bold text-slate-900 leading-snug">
                  {previewAuthorName}
                </h4>
                <p className="text-xs text-slate-600 mt-1 leading-relaxed">
                  {previewNote}
                </p>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="pt-3 border-t border-slate-100 flex items-center justify-end gap-2.5">
            <button
              type="button"
              onClick={onClose}
              disabled={isSaving}
              className="px-4 py-2.5 rounded-xl border border-slate-300 text-slate-700 text-xs font-bold hover:bg-slate-50 transition-colors cursor-pointer"
            >
              Batal
            </button>
            <button
              type="submit"
              disabled={isSaving}
              className="px-5 py-2.5 rounded-xl bg-amber-600 hover:bg-amber-700 active:bg-amber-800 text-white text-xs font-bold shadow-xs flex items-center gap-1.5 transition-all cursor-pointer disabled:opacity-50"
            >
              <Save className="w-3.5 h-3.5" />
              <span>{isSaving ? 'Menyimpan...' : 'Simpan Perubahan Teks'}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
